import React, { useState, useMemo } from 'react';
import { Popup } from 'semantic-ui-react';
import SummaryCodeAmountChips from '../../PayCalendarDetailsModal/SummaryCodeAmountChips';
import { formatCurrency } from '../services';

/**
 * Hover popup for totals-row columns (Write-Offs, Adjustments, Financial Notes).
 *
 * Displays a summary code/chip bar at the top — each chip represents one code/category
 * and its total amount across all currently-filtered providers. Clicking a chip filters
 * the table below to show only that code's transactions, sorted from the biggest
 * absolute amount down to the smallest (enabling quick analytics on biggest-impact codes).
 *
 * The data passed via `items` must already reflect the parent filter state from
 * CdpReports / CdpReportsTableByDentists / ReportFilterControls.
 *
 * Props:
 *  - trigger       {ReactNode} — the cell content that triggers the popup on hover
 *  - items         {Array}     — flat array of transaction records (already filtered)
 *  - codeField     {string}    — field name to group by (e.g. 'Writeoff_Code', 'ADJ_Type', 'category')
 *  - title         {string}    — label shown above the chip bar
 *  - columns       {Array}     — [{ key, label, render? }] table column descriptors
 */
const TotalsColumnHoverPopup = ({ trigger, items = [], codeField, title, columns }) => {
    const [selectedCode, setSelectedCode] = useState(null);

    const filteredItems = useMemo(() => {
        if (!selectedCode) return items;
        return items.filter(item => item[codeField] === selectedCode);
    }, [items, selectedCode, codeField]);

    // Sort from biggest absolute price to smallest so the highest-impact items appear first.
    // For write-offs this surfaces "biggest negative number" (largest write-off amount) first.
    const sortedItems = useMemo(
        () => [...filteredItems].sort((a, b) => Math.abs(b.price) - Math.abs(a.price)),
        [filteredItems]
    );

    const totalAmount = useMemo(
        () => sortedItems.reduce((sum, item) => sum + (item.price || 0), 0),
        [sortedItems]
    );

    // If there is no data, render the trigger as-is (no popup).
    if (!items.length) {
        return <span>{trigger}</span>;
    }

    return (
        <Popup
            on="hover"
            hoverable
            position="top center"
            trigger={
                <span
                    className="cursorPointer"
                    style={{ textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
                >
                    {trigger}
                </span>
            }
            style={{ maxWidth: 780, padding: '0.75rem', zIndex: 1050 }}
        >
            <div style={{ width: 740 }}>
                {/* Sticky chip bar ------------------------------------------------- */}
                <div style={{
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    paddingBottom: '0.5rem',
                    marginBottom: '0.5rem',
                }}>
                    <SummaryCodeAmountChips
                        title={title}
                        name={codeField}
                        value="price"
                        data={items}
                        selectedCode={selectedCode}
                        onCodeClick={setSelectedCode}
                    />
                    {selectedCode && (
                        <div style={{ marginTop: 4, fontSize: '0.82em', color: 'rgba(0,0,0,.55)' }}>
                            Showing <b>{filteredItems.length}</b> item(s) for&nbsp;<b>{selectedCode}</b>
                            &nbsp;— sorted biggest to smallest
                        </div>
                    )}
                    {!selectedCode && (
                        <div style={{ marginTop: 4, fontSize: '0.82em', color: 'rgba(0,0,0,.40)' }}>
                            Click a code above to filter the table and analyse by biggest amount
                        </div>
                    )}
                </div>

                {/* Scrollable data table ------------------------------------------ */}
                <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                    <table
                        className="table table-striped table-sm my-0"
                        style={{ fontSize: '0.82em', tableLayout: 'auto' }}
                    >
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        style={{
                                            whiteSpace: 'nowrap',
                                            position: 'sticky',
                                            top: 0,
                                            background: '#f8f8f8',
                                            zIndex: 1,
                                        }}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} style={{ textAlign: 'center', color: 'rgba(0,0,0,.5)' }}>
                                        No records
                                    </td>
                                </tr>
                            ) : (
                                sortedItems.map((item, i) => (
                                    <tr key={i}>
                                        {columns.map(col => (
                                            <td
                                                key={col.key}
                                                style={{
                                                    maxWidth: 160,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={col.render ? undefined : String(item[col.key] ?? '')}
                                            >
                                                {col.render ? col.render(item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #dee2e6' }}>
                                <td colSpan={columns.length - 1}>
                                    Total — {sortedItems.length} item{sortedItems.length !== 1 ? 's' : ''}
                                    {selectedCode ? ` (${selectedCode})` : ' (all codes)'}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(totalAmount)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </Popup>
    );
};

export default TotalsColumnHoverPopup;
